import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { z } from "zod";
import { AddLanguageFormSchema } from "../../schemas";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/Form";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import useLanguageService from "../../api/lanugages/language.service";
import { Language } from "../../api/lanugages/language.responses";
import { LuChevronDown } from "react-icons/lu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/Dropdown";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { RouteAction, RouteActionKind } from "../../Reducer";
import { RouteState } from "../../api/edits/editRoute.responses";
import { useSaveRouteData } from "../../hooks/useSaveRouteData";
import { toast } from "sonner";

type AddLanguageFormValues = z.infer<typeof AddLanguageFormSchema>;

interface AddLanguageFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  routeDispatch: React.Dispatch<RouteAction>;
  routeState: RouteState;
  setCurrentLanguage: React.Dispatch<React.SetStateAction<string>>;
}

function AddLanguageForm({
  open,
  onOpenChange,
  routeDispatch,
  routeState,
  setCurrentLanguage,
}: AddLanguageFormProps) {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const { getLanguages } = useLanguageService();
  const { saveRouteData } = useSaveRouteData();

  const form = useForm<AddLanguageFormValues>({
    resolver: zodResolver(AddLanguageFormSchema),
    defaultValues: {
      routeName: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    async function fetchLanguages() {
      try {
        const response = await getLanguages();
        setLanguages(
          response.data.filter(
            (language) => !Object.prototype.hasOwnProperty.call(routeState.routeTranslations, language.code)
          )
        );
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? err?.message ?? "Failed to load languages");
      }
    }
    fetchLanguages();
    setSelectedLanguage(null);
    form.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: AddLanguageFormValues) => {
    if (!selectedLanguage) {
      form.setError("routeName", { message: "Please select a language first" });
      return;
    }

    const tempRouteState: RouteState = {
      ...routeState,
      routeTranslations: {
        ...routeState.routeTranslations,
        [selectedLanguage.code]: {
          id: 0,
          name: values.routeName,
          description: values.description,
          languageName: selectedLanguage.name,
        },
      },
      pointsOfInterest: routeState.pointsOfInterest.map((poi) => ({
        ...poi,
        pointOfInterestTranslations: {
          ...poi.pointOfInterestTranslations,
          [selectedLanguage.code]: { id: 0, name: "Enter name", text: "Enter text" },
        },
      })),
    };

    await toast.promise(
      saveRouteData(tempRouteState),
      {
        loading: "Adding language...",
        success: () => {
          routeDispatch({
            type: RouteActionKind.ADD_LANGUAGE,
            payload: {
              ...selectedLanguage,
              initialName: values.routeName,
              initialDescription: values.description ?? "",
            },
          });
          setCurrentLanguage(selectedLanguage.code);
          onOpenChange(false);
          return "Language added";
        },
        error: (err: any) =>
          `Failed to add language: ${err?.response?.data?.message ?? err?.message ?? "Unknown error"}`,
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Language</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between">
                    {selectedLanguage ? selectedLanguage.name : "Select language"}
                    <LuChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  {languages.map((language) => (
                    <DropdownMenuItem
                      key={language.code}
                      onClick={() => setSelectedLanguage(language)}
                    >
                      {language.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <FormField
              control={form.control}
              name="routeName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Route name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter route name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter route description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p className="text-sm text-muted-foreground">
              Adding a language will automatically save the entire route.
            </p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Adding..." : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddLanguageForm;
